// Utility functions for getting client IP address

/**
 * Get client IP address using a public IP service
 * @returns {Promise<string>} - Client IP address
 */
export const getClientIP = async () => {
  try {
    // Try multiple IP services for redundancy
    const services = [
      'https://api.ipify.org?format=json',
      'https://api.ip.sb/ip',
      'https://api.myip.com',
      'https://ipapi.co/json'
    ];

    for (const service of services) {
      try {
        const response = await fetch(service, {
          method: 'GET',
          timeout: 5000
        });
        
        if (response.ok) {
          const data = await response.text();
          
          // Handle different response formats
          if (service.includes('ipify')) {
            const json = JSON.parse(data);
            return json.ip;
          } else if (service.includes('ip.sb')) {
            return data.trim();
          } else if (service.includes('myip.com')) {
            const json = JSON.parse(data);
            return json.ip;
          } else if (service.includes('ipapi.co')) {
            const json = JSON.parse(data);
            return json.ip;
          }
        }
      } catch (error) {
        console.warn(`Failed to get IP from ${service}:`, error);
        continue;
      }
    }
    
    throw new Error('All IP services failed');
  } catch (error) {
    console.error('Error getting client IP:', error);
    return 'Unknown';
  }
};

/**
 * Get client IP and add it to request headers
 * @param {Object} config - Axios config object
 * @returns {Promise<Object>} - Updated config with IP headers
 */
export const addIPToRequest = async (config) => {
  try {
    const clientIP = await getClientIP();
    
    // Add IP to headers
    config.headers = {
      ...config.headers,
      'X-Client-IP': clientIP,
      'X-Real-IP': clientIP
    };
    
    console.log('📍 Client IP added to request:', clientIP);
  } catch (error) {
    console.warn('Could not get client IP:', error);
  }
  
  return config;
};

/**
 * Get IP address synchronously (cached)
 */
let cachedIP = null;
let ipPromise = null;

export const getCachedIP = async () => {
  if (cachedIP) {
    return cachedIP;
  }
  
  if (ipPromise) {
    return ipPromise;
  }
  
  ipPromise = getClientIP().then(ip => {
    cachedIP = ip;
    return ip;
  });
  
  return ipPromise;
}; 